import { customFilterTypes, formModes } from "shared/constants/allocationsEnums";
import { forwardRef, useImperativeHandle, useState } from "react";

import Button from "shared/components/inputs/mui/Button";
import Check from "shared/components/utils/icons/Check";
import Close from "shared/components/utils/icons/Close";
import { Container } from "react-bootstrap";
import IconButton from "shared/components/inputs/button/IconButton";
import Left from "shared/components/utils/icons/Left";
import Link from "shared/components/navigation/links/AnchorLink";
import Modal from "react-bootstrap/Modal";
import PropTypes from "prop-types";
import Text from "shared/components/data-display/mui/Text";
import TextInputWithValidation from "shared/components/inputs/text-input/TextInputWithValidation";
import { useCustomFilters } from "features/allocations/api/useCustomFilters";
import { useForm } from "react-hook-form";
import { useValidationError } from "shared/hooks/errors/useValidationError";
import { Action, Back, Content, ContentWrapper, FormField, ModalControl, useStyles } from "./CustomFilter.styles";

const CustomFilter = forwardRef((props, ref) => {
    const { createCustomFilter, updateCustomFilter, deleteCustomFilter } =
        useCustomFilters();
    const { onCreate, onUpdate, onDelete } = props;
    const {
        control,
        setError,
        trigger,
        formState: { errors },
    } = useForm({
        mode: "onChange",
    });
    const [title, setTitle] = useState("");
    const [mode, setMode] = useState(formModes.ADD);
    const [show, setShow] = useState(false);
    const [id, setId] = useState("");
    const [name, setName] = useState("");
    const [previousName, setPreviousName] = useState("");
    const [data, setData] = useState(null);
    const classes = useStyles();

    const { handleFormErrorResponse } = useValidationError();

    useImperativeHandle(ref, () => ({
        add(data) {
            setData(data);
            setId("");
            setName("");
            setMode(formModes.ADD);
            setTitle("Save Filters");
            setShow(true);
        },
        rename(id, name) {
            setPreviousName(name);
            setId(id);
            setName(name);
            setMode(formModes.UPDATE);
            setTitle("Rename Filters");
            setShow(true);
        },
        delete(id, name) {
            setPreviousName(name);
            setId(id);
            setName(name);
            setMode(formModes.DELETE);
            setTitle("Delete Saved Filters");
            setShow(true);
        },
    }));

    const handleClose = () => {
        setMode(props.mode);
        setShow(false);
    };

    const handleShow = () => {
        setShow(true);
    };

    const handleSave = async function () {
        switch (mode) {
            case formModes.ADD: {

                const selection = JSON.stringify(data);
               
                try {
                    const payload = await createCustomFilter({
                        type: customFilterTypes.ALLOCATION,
                        name: name,
                        content: selection,
                    });

                    setMode(formModes.ADDED);

                    if (onCreate) {
                        onCreate(payload);
                    }

                } catch (error) {
                    handleFormErrorResponse(error, setError);
                }
                break;
            }
            case formModes.UPDATE:
                try {
                    
                    await updateCustomFilter({
                        type: customFilterTypes.ALLOCATION,
                        id: id,
                        name: name,
                    });

                    setMode(formModes.UPDATED);

                    if (onUpdate) {
                        onUpdate({ id, name });
                    }
                    
                } catch (error) {
                    handleFormErrorResponse(error, setError);
                }
                break;
            default:
                break;
        }
    };
    
    const handleDelete = async function () {
        try {
            
            const payload = await deleteCustomFilter([{ name: "$id", value: id }]);
            
            if (payload.error) {
                throw payload;
            }

            setMode(formModes.DELETED);

            if (onDelete) {
                onDelete(id);
            }
            
        } catch (error) {
            handleFormErrorResponse(error, setError);
        }
    };

    return (
        <>
            <ModalControl onClick={handleShow}>{props.children}</ModalControl>
            <Modal
                show={show}
                onHide={() => handleClose()}
                backdrop={globalThis.config.modal.static}
                keyboard={globalThis.config.modal.keyboard}
                centered
                animation={false}
                className={classes.root}
            >
                <Modal.Header>
                    <Back onClick={handleClose}>
                        <Left circle={false} />
                        <Text variant="small">Back to Filters</Text>
                    </Back>
                    <IconButton
                        icon={<Close />}
                        onClick={() => handleClose()}
                    />
                </Modal.Header>
                <Modal.Body>
                    <Container fluid>
                        <ContentWrapper>
                            <Content>
                                {(mode === formModes.ADD ||
                                    mode === formModes.UPDATE) && (
                                        <>
                                            <Text variant="xlarge">{title}</Text>
                                            <FormField>
                                                <TextInputWithValidation
                                                    required={true}
                                                    maxLength={40}
                                                    label="Name your saved filters"
                                                    control={control}
                                                    name="name"
                                                    errors={errors}
                                                    onChange={(event) =>
                                                        setName(event.target.value)
                                                    }
                                                    value={name}
                                                    defaultValue={name}
                                                />
                                            </FormField>
                                            <Action>
                                                <Link
                                                    variant="secondary"
                                                    onClick={() => handleClose()}
                                                >
                                                    <Text variant="smallLight">
                                                        Cancel
                                                    </Text>
                                                </Link>
                                                <Button
                                                    onClick={async () => {
                                                        const valid =
                                                            await trigger();
                                                        if (valid) {
                                                            handleSave();
                                                        }
                                                    }}
                                                >
                                                    Save
                                                </Button>
                                            </Action>
                                        </>
                                    )}
                                {mode === formModes.DELETE && (
                                    <>
                                        <Text variant="xlarge">{title}</Text>
                                        <FormField>
                                            <Text variant="smallLight">
                                                You are about to delete{" "}
                                                <strong>{name}</strong>.<br />
                                                <br />
                                                Do you wish to continue?
                                            </Text>
                                        </FormField>
                                        <Action>
                                            <Link
                                                variant="secondary"
                                                onClick={handleClose}
                                            >
                                                <Text variant="smallLight">
                                                    Cancel
                                                </Text>
                                            </Link>
                                            <Button
                                                onClick={async () => {
                                                    handleDelete();
                                                }}
                                            >
                                                Yes, Delete
                                            </Button>
                                        </Action>
                                    </>
                                )}
                                {mode === formModes.ADDED && (
                                    <>
                                        <Text variant="xlarge">
                                            Filters Saved
                                        </Text>
                                        <FormField>
                                            <Text variant="smallLight">
                                                <strong>{name}</strong> has been
                                                saved.
                                            </Text>
                                        </FormField>
                                        <Action>
                                            <Button onClick={handleClose}>
                                                View Filters
                                            </Button>
                                        </Action>
                                    </>
                                )}
                                {mode === formModes.UPDATED && (
                                    <>
                                        <Check size="24" />
                                        <Text variant="xlarge">
                                            Filters Renamed
                                        </Text>
                                        <FormField>
                                            <Text variant="smallLight">
                                                <strong>{previousName}</strong>{" "}
                                                has been renamed to{" "}
                                                <strong>{name}</strong>.
                                            </Text>
                                        </FormField>
                                        <Action>
                                            <Button onClick={handleClose}>
                                                View Filters
                                            </Button>
                                        </Action>
                                    </>
                                )}
                                {mode === formModes.DELETED && (
                                    <>
                                        <Text variant="xlarge">
                                            Filters Deleted
                                        </Text>
                                        <FormField>
                                            <Text variant="smallLight">
                                                <strong>{name}</strong> has been
                                                deleted.
                                            </Text>
                                        </FormField>
                                        <Action>
                                            <Button onClick={handleClose}>
                                                View Filters
                                            </Button>
                                        </Action>
                                    </>
                                )}
                            </Content>
                        </ContentWrapper>
                    </Container>
                </Modal.Body>
            </Modal>
        </>
    );
});

CustomFilter.propTypes = {
    onCreate: PropTypes.func,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    mode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.node,
};

export default CustomFilter;
